import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import ElementListSearchBar from '@editor/components/ElementListPane/components/ElementListSearchBar'
import usePlanTableEditMode from '@editor/hooks/usePlanTableEditMode'
import { usePlanTableStore } from '@editorStores'
import { useSelectedPlanElementId } from '@editorHooks'
import type { PlanTable } from 'src/domain/planTables/types'
import TableRow from './TableRow'

const matchesSearch = (planTable: PlanTable, search: string): boolean => {
  if (!search) return true
  const normalized = search.trim().toLowerCase()
  return planTable.name.toLowerCase().includes(normalized)
}

/**
 * Project-wide plan tables as a list pane. Lists every table in the scene; selecting a row opens
 * its settings in the adjacent drawer (PlanTableForm).
 */
const TablesListPane = (): ReactElement => {
  const tables = usePlanTableStore(state => state.tables)
  const selectedId = useSelectedPlanElementId('table')
  const setIsVisible = usePlanTableStore(state => state.setIsVisible)
  const { enterPlanTableEdit } = usePlanTableEditMode()

  useEffect(() => {
    setIsVisible(true)
  }, [setIsVisible])

  const [search, setSearch] = useState('')

  const filteredTables = useMemo(
    () => tables.filter(planTable => matchesSearch(planTable, search)),
    [tables, search],
  )

  const handleSelect = useCallback((id: string) => enterPlanTableEdit(id), [enterPlanTableEdit])

  return (
    <Box
      data-cy="tables-list-pane"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: 'common.white',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.75,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ElementListSearchBar value={search} onChange={setSearch} />
        </Box>
      </Box>

      {filteredTables.length === 0 ? (
        <Box
          sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
        >
          <Typography variant="body2" color="text.secondary" data-cy="tables-list-empty">
            {tables.length === 0 ? 'Keine Tabellen' : 'Keine Treffer'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {filteredTables.map(planTable => (
            <TableRow
              key={planTable.id}
              planTable={planTable}
              selected={selectedId === planTable.id}
              onSelect={handleSelect}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default TablesListPane
